/**
 * The title screen.
 *
 * There was not one. The game booted into the prologue chamber, and after that
 * into the setup screen, so nothing ever said what this was or gave the player
 * a place to stand that was not already a decision. That is the difference
 * between a build and a game.
 *
 * Follows the `runSetup.ts` conventions exactly: the screen renders a view
 * model, reports intent through callbacks, mutates nothing, and imports only
 * the DOM kit.
 *
 * It had no Continue entry until issue #51, because nothing in the codebase
 * read `SaveSystem.load().run` and the button would have started a new run
 * under an old name. It has one now, and the honesty it needs lives in the
 * item's `note` rather than in this file: a resumed run is rebuilt from its
 * seed, so the *state* of the cave does not come back. `buildTitleVM` in
 * `core/runSetupAdapter.ts` writes that sentence; `meta/runState.ts`'s
 * `isResumable` records the whole decision, field by field.
 */

import { el, injectStyle } from './style';
import { injectShellStyle } from './shellStyle';

export type TitleAction = 'continue' | 'newRun' | 'settings' | 'lab' | 'workshop' | 'prologue';

export interface TitleItemVM {
  id: TitleAction;
  label: string;
  /** The one-line honest description of what pressing this does. */
  note: string;
}

export interface TitleKeyVM {
  keys: string;
  /** The gamepad equivalent — issue #193. Shown instead of `keys` while a pad is the active device. */
  pad: string;
  what: string;
}

export interface TitleVM {
  title: string;
  tagline: string;
  /** Null for a profile that has never finished a run — nothing to report yet. */
  summary: string | null;
  items: readonly TitleItemVM[];
  controls: readonly TitleKeyVM[];
}

export interface TitleCallbacks {
  onChoose?(action: TitleAction): void;
}

export class TitleScreen {
  readonly root: HTMLElement;

  private hostEl: HTMLElement;
  private cb: TitleCallbacks;
  private ownsRootClass = false;

  private h1: HTMLElement;
  private tag: HTMLElement;
  private summary: HTMLElement;
  private menu: HTMLElement;
  private keys: HTMLElement;

  private items: readonly TitleItemVM[] = [];
  private els: HTMLElement[] = [];
  private cursor = 0;
  private controls: readonly TitleKeyVM[] = [];
  /** Whether the legend shows `k.pad` instead of `k.keys` — issue #193. */
  private padActive = false;

  private listeners: Array<[EventTarget, string, EventListener, boolean]> = [];

  constructor(host: HTMLElement, cb: TitleCallbacks = {}) {
    this.hostEl = host;
    this.cb = cb;
    injectStyle();
    injectShellStyle();
    if (!host.closest('.ui-root')) {
      host.classList.add('ui-root');
      this.ownsRootClass = true;
    }

    this.root = el('div', 'ui-layer sh-layer');
    this.root.style.pointerEvents = 'auto';
    this.root.tabIndex = -1;
    this.root.hidden = true;
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'Title screen');

    const box = el('div', 'sh-box');

    const brand = el('div', 'sh-brand');
    this.h1 = document.createElement('h1');
    this.tag = el('p', 'sh-tag');
    this.summary = el('div', 'sh-summary');
    brand.append(this.h1, el('div', 'sh-rule'), this.tag, this.summary);

    this.menu = el('div', 'sh-menu');
    this.menu.setAttribute('role', 'menu');
    this.keys = el('div', 'sh-keys');

    const foot = el('div', 'sh-foot');
    foot.append(
      el('span', '', '↑↓ choose'),
      el('span', '', 'Enter begin'),
      el('span', '', 'Esc pauses in play'),
      // The pad line is on the screen for the same reason every other line is:
      // a controller-only player has no way to discover that the d-pad works
      // here, and until #78 it did not.
      el('span', '', 'Pad: d-pad choose, A confirm'),
    );

    box.append(brand, this.menu, this.keys, foot);
    this.root.append(box);
    host.append(this.root);

    this.on(this.root, 'keydown', ((e: KeyboardEvent) => this.onKey(e)) as EventListener);
  }

  private on(t: EventTarget, type: string, fn: EventListener, capture = false): void {
    t.addEventListener(type, fn, capture);
    this.listeners.push([t, type, fn, capture]);
  }

  update(vm: TitleVM): void {
    if (this.h1.textContent !== vm.title) this.h1.textContent = vm.title;
    if (this.tag.textContent !== vm.tagline) this.tag.textContent = vm.tagline;
    this.summary.textContent = vm.summary ?? '';
    this.summary.hidden = vm.summary === null;

    const sig = vm.items.map((i) => i.id + i.label + i.note).join('|');
    if (this.menu.dataset.sig !== sig) {
      this.menu.dataset.sig = sig;
      this.menu.textContent = '';
      this.els = [];
      this.items = vm.items;
      for (const it of vm.items) {
        const b = document.createElement('button');
        b.className = 'sh-item';
        b.type = 'button';
        b.setAttribute('role', 'menuitem');
        b.append(el('b', '', it.label), el('i', '', it.note));
        b.addEventListener('click', () => this.choose(it.id));
        b.addEventListener('mousemove', () => {
          const i = this.items.indexOf(it);
          if (i >= 0 && i !== this.cursor) {
            this.cursor = i;
            this.paint();
          }
        });
        this.menu.append(b);
        this.els.push(b);
      }
      if (this.cursor >= this.els.length) this.cursor = 0;
      this.paint();
    }

    this.controls = vm.controls;
    const ksig = vm.controls.map((k) => k.keys + k.pad + k.what).join('|');
    if (this.keys.dataset.sig !== ksig) {
      this.keys.dataset.sig = ksig;
      this.renderKeys();
    }
  }

  /**
   * Redraw the legend from `this.controls`, picking `k.pad` or `k.keys` per
   * `this.padActive` — issue #193. Split out of `update()` so `setPadActive`
   * can call it without waiting for the VM to change: the legend has to flip
   * the instant the player picks up a pad, not on the next unrelated re-render.
   */
  private renderKeys(): void {
    this.keys.textContent = '';
    for (const k of this.controls) {
      const s = el('span');
      s.append(el('b', '', this.padActive ? k.pad : k.keys), document.createTextNode(' ' + k.what));
      this.keys.append(s);
    }
  }

  /**
   * Switch the legend between keyboard and pad labels — issue #193. `main.ts`
   * calls this off its own "last input device" tracking; see its header for
   * the heuristic, since this class has no way to observe the pad itself.
   */
  setPadActive(active: boolean): void {
    if (this.padActive === active) return;
    this.padActive = active;
    this.renderKeys();
  }

  private paint(): void {
    for (let i = 0; i < this.els.length; i++) {
      const on = i === this.cursor ? '1' : '0';
      if (this.els[i].dataset.on !== on) this.els[i].dataset.on = on;
    }
  }

  private choose(id: TitleAction): void {
    this.cb.onChoose?.(id);
  }

  /**
   * Drive the cursor from a gamepad — issue #78.
   *
   * Public, and the same shape as `RunSetupScreen.handleKey`, so an integrator
   * holding a pad can move this screen without a second window listener. It
   * routes into the *same* handler the keyboard uses rather than duplicating
   * the cursor arithmetic, which is the whole reason the pad speaks in keys:
   * `main.ts` builds the record with `padKeyEvent`, `menuPad.ts` decides which
   * key each intent is, and nothing about the title's five-item column is
   * written down twice.
   */
  handlePadKey(e: KeyboardEvent): void {
    this.onKey(e);
  }

  private onKey(e: KeyboardEvent): void {
    const n = this.els.length;
    if (n === 0) return;
    if (e.code === 'ArrowDown' || e.code === 'KeyS') {
      this.cursor = (this.cursor + 1) % n;
    } else if (e.code === 'ArrowUp' || e.code === 'KeyW') {
      this.cursor = (this.cursor - 1 + n) % n;
    } else if (e.code === 'Enter' || e.code === 'Space' || e.code === 'NumpadEnter') {
      this.choose(this.items[this.cursor].id);
    } else {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    this.paint();
  }

  show(): void {
    this.root.hidden = false;
    this.root.focus();
  }

  hide(): void {
    this.root.hidden = true;
  }

  destroy(): void {
    for (const [t, type, fn, capture] of this.listeners) t.removeEventListener(type, fn, capture);
    this.listeners.length = 0;
    this.root.remove();
    if (this.ownsRootClass) this.hostEl.classList.remove('ui-root');
  }
}
